import { useOutletContext } from "react-router-dom";
import { Box } from "@dealer-center/core-components";
import {
  FeeManager,
  FeeRowSkeleton
} from "@dealer-center/core-feemanager-react";
import { useCorgi } from "@dealer-center/ddc-pages-fee-manager-fetch";
import { useCC9Flags } from "@dealer-center/core-third-party-launch-darkly";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";

const BASE_PATH = "/landing/nxlambda/feeManager";

export const DdcFeeManager = () => {
  const { ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
  }>();
  const flags = useCC9Flags();

  const tenantId = (ccUserInfo?.accountId ?? "").trim();
  const tenantUser = ccUserInfo?.internalUserId ?? ccUserInfo?.userId ?? "";
  // The lambda's tenant guard authorizes every request against this acting account/user.
  const requestParams = {
    asAccount: tenantId,
    asUser: ccUserInfo?.userId ?? ""
  };

  // ccUserInfo absence (misconfigured route) denies access.
  // Both accountPermissions and userPermissions must include INVENTORY.FEEMANAGER.
  const hasAccess =
    !!ccUserInfo &&
    tenantId !== "" &&
    tenantId.toLowerCase() !== "ddc" &&
    ccUserInfo.accountPermissions.includes("INVENTORY.FEEMANAGER") &&
    ccUserInfo.userPermissions.includes("INVENTORY.FEEMANAGER");

  // LD evaluates against the acting account (tenantId) via the provider's account context.
  const shouldShowCommonOrgToggle =
    !!ccUserInfo?.internalUserId &&
    !!flags["cc9.fee-manager.use-common-org-id"];

  // Skip the corgi lookup when core will only render its NO_ACCESS alert.
  const {
    data: corgi,
    isError: isCorgiError,
    isPending: isCorgiPending,
    refetch: refetchCorgi
  } = useCorgi({
    basePath: BASE_PATH,
    accountId: hasAccess ? tenantId : "",
    requestParams
  });

  // A failed lookup mounts with a null corgi; core offers a retry if any fees are COMMON_ORG-sourced.
  if (hasAccess && isCorgiPending) {
    return (
      <Box aria-busy="true" data-testid="ddc-fee-manager-loading">
        <FeeRowSkeleton count={5} />
      </Box>
    );
  }

  const resolvedCorgi = hasAccess ? (corgi ?? null) : null;

  return (
    <FeeManager
      basePath={BASE_PATH}
      clientId="dealercenter"
      corgi={resolvedCorgi}
      hasAccess={hasAccess}
      isCorgiLoadFailed={hasAccess && isCorgiError}
      key={`DDC:${tenantId}:${resolvedCorgi ?? ""}`}
      requestParams={requestParams}
      showCommonOrgToggle={shouldShowCommonOrgToggle}
      tenantId={tenantId}
      tenantType="DDC"
      tenantUser={tenantUser}
      onRetryCorgi={() => refetchCorgi()}
    />
  );
};
